import { COLORS } from '@avalara/bat-apps-shared/constants';
import { t } from '@avalara/bat-apps-shared/i18n';
import { SCol, SLoader, SRow } from '@avalara/skylab-sdk/react';
import { Fragment, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { formatPrice } from '../../../../shared/helpers';
import { isFormIncomplete } from '../../../lib/shared-signup';
import { getOrderSalesTax } from '../../../lib/side-effects/order';
import { selectState } from '../../../selectors';
import {
  getDiscountAmount,
  getOrderPreDiscountSubtotal,
  getOrderSubtotal,
  getOrderSummary,
  getOrderTotal,
  getPlanSpecificBillingTerms,
  getProductItems,
  getSelectedPlan,
  getShouldShowPreDiscountedSubtotal,
} from '../../../selectors/order';
import { setOrderQuantity } from '../../../store/order';
import { getBillingTerms } from '../billing-terms';
import { OrangeBorderTermsBox, OrangeHorizontalRule, OrderSummaryContainer } from '../styled/order-summary';
import AdjustableQuantityItem from './adjustable-quantity-item';
import Discount from './discount';
import IncludedItemsBreakdown from './included-items-breakdown';
import OrderItem from './order-item';
import Subtext from './order-item-subtext';
import OrderSummaryHeader from './order-summary-header';

const orangeTextStyle = {
  color: COLORS.darkOrange,
};

const OrderSummary = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    account: { billingContact, connector },
    offer,
    order: { discountName, discountPercent, quantity, salesTax, isFetchingSalesTax },
    sharedSignup,
  } = useSelector(selectState);

  const { adjustableQuantity, isEmeaOffer, offerFlow, taxCode } = offer;

  const productItems = useSelector(getProductItems);
  const selectedPlan = useSelector(getSelectedPlan);
  const shouldShowPreDiscountedSubtotal = useSelector(getShouldShowPreDiscountedSubtotal);
  const preDiscountSubtotal = useSelector(getOrderPreDiscountSubtotal);
  const discountAmount = useSelector(getDiscountAmount);
  const subtotal = useSelector(getOrderSubtotal);
  const totalPrice = useSelector(getOrderTotal);
  const formIncomplete = isFormIncomplete(sharedSignup);
  const {
    avataxRequired,
    additionalItems,
    heading,
    subheading,
    planIncludes,
    secondaryHeading,
    secondaryItems,
    showBillingTermsInOrderSummary,
    showDiscountedSubTotal,
    totalSubtext,
    totalText,
  } = useSelector(getOrderSummary);

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    dispatch(getOrderSalesTax());
  }, [billingContact, formIncomplete, subtotal]);

  const isDiscountApplied = !!discountPercent && discountPercent > 0;
  const currency = selectedPlan?.currency || undefined;
  const planSpecificBillingTerms = useSelector(getPlanSpecificBillingTerms);
  const queryParams = new URLSearchParams(location.search);
  const disableQuantity = queryParams.get('quantity') !== null;
  const isFreeTrialFlow = offerFlow === 'free-trial';

  return (
    <OrderSummaryContainer>
      <OrderSummaryHeader offerFlow={offerFlow} />

      {!!heading && (
        <SRow className='pad-top-xs pad-bottom-xs'>
          <SCol span='auto'>
            <h3 data-ref='buy.order-summary.offer-name-heading'>{heading}</h3>
            {subheading && (
              <span data-ref='buy.order-summary.offer-sub-heading' className='font-italic'>
                {subheading}
              </span>
            )}
          </SCol>
        </SRow>
      )}

      {!!productItems.length && (
        <div className='pad-bottom-sm'>
          {adjustableQuantity && productItems[0] != null ? (
            <Fragment>
              <AdjustableQuantityItem
                currency={currency}
                quantity={quantity}
                price={productItems[0]?.price}
                disabled={disableQuantity}
                strikethrough={isDiscountApplied}
                subtotal={preDiscountSubtotal}
                onChangeQuantity={(e) => dispatch(setOrderQuantity({ quantity: Number(e.target.value), offer }))}
              />
              {productItems[0].subtext && <Subtext text={productItems[0].subtext} />}
            </Fragment>
          ) : (
            productItems.map((productItem) => (
              <OrderItem
                key={productItem.name}
                name={offerFlow === 'add-integration' ? connector.name : productItem.name}
                price={formatPrice(productItem.price, 2, currency)}
                strikethrough={isDiscountApplied && !shouldShowPreDiscountedSubtotal}
                subtext={productItem.subtext}
              />
            ))
          )}

          {shouldShowPreDiscountedSubtotal && (
            <Fragment>
              <hr className='margin-left-sm margin-right-sm margin-top-xs' />
              <SRow className='pad-top-xs'>
                <SCol span='8'>{t('client.general.subtotal')}</SCol>
                <SCol
                  span='4'
                  data-ref='buy.order-summary.pre-discounted-total'
                  className='text-right text-strikethrough'
                >
                  {formatPrice(preDiscountSubtotal, 2, currency)}
                </SCol>
              </SRow>
            </Fragment>
          )}

          {isDiscountApplied && (
            <Discount
              currency={currency}
              discountAmount={discountAmount}
              discountName={discountName}
              showDiscountedSubTotal={showDiscountedSubTotal}
              subtotal={subtotal}
            />
          )}

          {!!taxCode && !isEmeaOffer && (
            <SRow className='pad-top-xs pad-bottom-xs'>
              <SCol span='6'>{t('client.general.sales-tax')}</SCol>
              <SCol span='6' className='text-right'>
                {isFetchingSalesTax && <SLoader loading={true} data-ref='tax-calculating-loader' className='small' />}
                {!isFetchingSalesTax &&
                  (salesTax == null ? (
                    <p data-ref='buy.order-summary.calculated-at-checkout' className='font-italic smaller-text'>
                      {t('client.general.calculated-at-checkout')}
                    </p>
                  ) : (
                    <p>{formatPrice(salesTax, 2, currency)}</p>
                  ))}
              </SCol>
            </SRow>
          )}

          {additionalItems?.map((item) => (
            <OrderItem
              key={`additionalItem${item.name}`}
              name={item.name}
              price={formatPrice(item.price, 2, currency)}
              subtext={item.subtext}
            />
          ))}

          <OrangeHorizontalRule className='margin-all-xs' />

          {!isFreeTrialFlow && (
            <span>
              <OrderItem name={totalText} price={formatPrice(totalPrice, 2, currency)} />
            </span>
          )}
          {isFreeTrialFlow && (
            <span>
              <OrderItem
                name={totalText}
                nameStyle={orangeTextStyle}
                priceStyle={orangeTextStyle}
                price={formatPrice(totalPrice, 2, currency)}
              />
            </span>
          )}

          <IncludedItemsBreakdown planIncludes={planIncludes} offerFlow={offerFlow} />

          {!!totalSubtext?.length && (
            <SRow>
              <SCol>
                {totalSubtext.map((text, i) => (
                  <div key={text} data-ref={`${offerFlow || ''}-total-subtext-${i}`}>
                    <Subtext text={text} />
                  </div>
                ))}
              </SCol>
            </SRow>
          )}
        </div>
      )}

      {(!!secondaryHeading || !!secondaryItems?.length || !!showBillingTermsInOrderSummary) && (
        <OrangeBorderTermsBox className='pad-top-sm pad-bottom-sm'>
          {secondaryHeading && (
            <SRow>
              <SCol>
                <p className='text-xs-strong text-uppercase pad-bottom-xs'>{secondaryHeading}</p>
              </SCol>
            </SRow>
          )}

          {secondaryItems?.map((item) => (
            <OrderItem
              key={`secondaryItem${item.name}`}
              name={item.name}
              price={formatPrice(item.price, 2, currency)}
              subtext={item.subtext}
            />
          ))}

          {showBillingTermsInOrderSummary && (
            <Fragment>
              <SRow>
                <SCol>
                  <p className='text-xs-strong text-uppercase pad-bottom-xs'>{t('client.general.billing-terms')}</p>
                </SCol>
              </SRow>
              {avataxRequired && (
                <SCol>
                  <p className='text-xs' data-ref='avatax-required-disclaimer'>
                    {t('client.general.this-product-requires-avatax')}
                  </p>
                </SCol>
              )}
              {offerFlow &&
                getBillingTerms(planSpecificBillingTerms, offerFlow).map((billingTerm, i) => (
                  <SCol key={billingTerm}>
                    <p className='pad-top-xs text-xs' data-ref={`order-summary-billing-terms.${offerFlow}.term-${i}`}>
                      {billingTerm}
                    </p>
                  </SCol>
                ))}
            </Fragment>
          )}
        </OrangeBorderTermsBox>
      )}
    </OrderSummaryContainer>
  );
};

export default OrderSummary;
